import { memo } from 'react';
import { Card, Divider, Stack, Typography } from '@mui/material';

import { modifyColor } from '@/modules/floorplanDesign/mappers/colors';

type DetailValue = number | string;

type Props = {
  title: string;
  total: number;
  unitLabel: string;
  unitValue: DetailValue;
  perUnitLabel: string;
  perUnitValue: DetailValue;
  color?: string;
};

type AreaDetailMetricProps = {
  label: string;
  value: DetailValue;
};

function AreaDetailMetricComponent({ label, value }: AreaDetailMetricProps) {
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Typography
        variant='body1'
        fontWeight={600}
        sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

const AreaDetailMetric = memo(AreaDetailMetricComponent);

function AreaDetailsCardComponent({
  title,
  total,
  unitLabel,
  unitValue,
  perUnitLabel,
  perUnitValue,
  color,
}: Props) {
  const backgroundColor = color ? modifyColor(color, false, '24') : 'background.default';
  const borderColor = color ? modifyColor(color, false, '80') : 'divider';

  return (
    <Card
      variant='outlined'
      sx={{
        width: '100%',
        p: 2,
        bgcolor: backgroundColor,
        borderColor,
        borderRadius: 1,
      }}
    >
      <Typography variant='subtitle1' fontWeight={600} noWrap>
        {title}
      </Typography>

      <Stack
        direction='row'
        divider={<Divider orientation='vertical' flexItem />}
        spacing={2}
        mt={2}
      >
        <Stack flex={1} minWidth={0} spacing={1.25}>
          <AreaDetailMetric label='Total' value={total} />
          <AreaDetailMetric label={unitLabel} value={unitValue} />
        </Stack>

        <Stack flex={1} minWidth={0} spacing={1.25}>
          <AreaDetailMetric label={perUnitLabel} value={perUnitValue} />
        </Stack>
      </Stack>
    </Card>
  );
}

export const AreaDetailsCard = memo(AreaDetailsCardComponent);
